import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import type { CommandEndpoint } from "@shared-core/data/object-ui-adapter";
import type {
  Command,
  CommandResult,
} from "@shared-core/domain/object-ui/contract";
import { notifyObjectUiChanged } from "@shared-core/domain/object-ui/hooks";
import {
  useObjectManifest,
  type ManifestIndex,
} from "@shared-core/domain/object-ui/manifest";
import {
  prefillOf,
  type PrefillParams,
} from "@shared-core/domain/object-ui/related-prefill";
import { CommandShell } from "./components/patterns/command-shell/CommandShell";
import type { KnownEntities } from "./components/patterns/command-shell/types";
import { COMMAND_WIDGETS } from "./object-ui-registry";

export interface OpenCommand {
  endpoint: CommandEndpoint;
  context: string;
  initialBody?: Record<string, unknown>;
  /** The body paths of `initialBody` taken from a parent record's related list: shown, not editable. */
  lockedPaths?: readonly string[];
  /** What the page called the records `initialBody` picks (a setting's typeahead handing over), so the dialog names them. */
  knownEntities?: KnownEntities;
  /** A create command: open what it created. */
  opensCreated?: boolean;
}

/**
 * A collection command (Add note…) as the shell opens it. Under a related
 * list, or a list reached through one, `params` are the parent record's: the
 * command's fields they name start filled and locked, the first dry run is
 * sent them, and every other field is left as the command makes it.
 */
export function collectionCommandOpening(
  index: ManifestIndex,
  command: Command,
  options: { context: string; params?: PrefillParams },
): OpenCommand {
  const declared = index.commandOf(command.name);
  const prefill = prefillOf(declared, options.params ?? {});
  return {
    endpoint: {
      command: declared,
      href: command.href,
      dryRunHref: command.dry_run_href,
    },
    context: options.context,
    // Opened under a parent record's list, the person stays there: the list reads again and shows what was added.
    ...(prefill.lockedPaths.length > 0
      ? { initialBody: prefill.body, lockedPaths: prefill.lockedPaths }
      : { opensCreated: true }),
  };
}

/**
 * The page side of running a command: one shell open at a time; on commit,
 * say what happened, read everything on screen again (brief: "commit, then
 * the record is read again"), and open what a create command made.
 */
export function useCommandHost() {
  const [open, setOpen] = useState<OpenCommand | null>(null);
  const navigate = useNavigate();
  const index = useObjectManifest();

  // The record the command ran on already shows the created record in one of its own related lists or child tables
  // (an alert's Tickets list holds the ticket Convert to ticket makes): the toast needn't offer a jump to it, and the
  // command that changed its subject this way (the alert leaves the open alerts) re-reads it below, which would churn
  // an action toast off the screen. A command that made the record somewhere the subject doesn't surface it (Add to
  // draft quote… on a Tharstern order, whose record has no draft quotes) is the one the toast offers.
  const subjectSurfaces = (target: string, object: string): boolean => {
    if (!index.isObjectKnown(target)) return false;
    const subject = index.objectOf(target);
    return (
      subject.related.some((r) => r.object === object) ||
      subject.children.some((c) => c.object === object || c.opens === object)
    );
  };

  const done = (command: OpenCommand, result: CommandResult) => {
    setOpen(null);
    const created = result.created;
    const createdHref = created ? index.hrefOf(created) : null;
    // Say what happened before re-reading the page (notifyObjectUiChanged re-reads the record on screen).
    if (command.opensCreated && createdHref) {
      toast.success(result.summary);
      notifyObjectUiChanged();
      navigate(createdHref);
      return;
    }
    // A command on one record that made another record elsewhere (Add to draft quote… on a Tharstern order made a draft
    // quote): the person stays where they are and the toast offers the new record.
    const target = command.endpoint.command.target;
    const offered =
      created &&
      createdHref &&
      created.object !== target &&
      index.isObjectKnown(created.object) &&
      index.objectOf(created.object).record &&
      !subjectSurfaces(target, created.object)
        ? {
            label: `Open ${index.objectOf(created.object).label.toLowerCase()} ${created.id}`,
            href: createdHref,
          }
        : null;
    if (offered) {
      toast.success(result.summary, {
        action: { label: offered.label, onClick: () => navigate(offered.href) },
      });
    } else {
      toast.success(result.summary);
    }
    notifyObjectUiChanged();
  };

  const shell = open ? (
    <CommandShell
      key={open.endpoint.href}
      endpoint={open.endpoint}
      context={open.context}
      widgets={COMMAND_WIDGETS}
      onClose={() => setOpen(null)}
      onDone={(result) => done(open, result)}
      {...(open.initialBody ? { initialBody: open.initialBody } : {})}
      {...(open.lockedPaths ? { lockedPaths: open.lockedPaths } : {})}
      {...(open.knownEntities ? { knownEntities: open.knownEntities } : {})}
    />
  ) : null;

  return { openCommand: setOpen, shell };
}
